import { motion } from 'framer-motion'
import { Bot, ArrowRight, Sparkles, User } from 'lucide-react'

interface StepProps {
  onNext: () => void
}

const Step1 = ({ onNext }: StepProps) => {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', duration: 0.8 }}
        className="relative mb-8"
      >
        <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-2xl shadow-primary-500/30">
          <Bot className="w-16 h-16 text-white" />
        </div>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center"
        >
          <Sparkles className="w-5 h-5 text-white" />
        </motion.div>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-4xl md:text-5xl font-bold mb-4"
      >
        Meet Your <span className="gradient-text">AI Twin</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-xl text-surface-600 dark:text-surface-400 max-w-2xl mb-8"
      >
        A digital assistant that truly knows you. Your AI Twin learns your unique style,
        preferences, and work patterns to provide personalized assistance that feels natural.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex items-center gap-8 mb-12"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-surface-200 dark:bg-surface-700 flex items-center justify-center">
            <User className="w-6 h-6 text-surface-600 dark:text-surface-400" />
          </div>
          <span className="text-surface-600 dark:text-surface-400">You</span>
        </div>

        <motion.div
          animate={{ x: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="flex items-center gap-2 text-primary-500"
        >
          <div className="w-2 h-2 rounded-full bg-primary-500" />
          <div className="w-2 h-2 rounded-full bg-primary-500/70" />
          <div className="w-2 h-2 rounded-full bg-primary-500/40" />
        </motion.div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <span className="text-surface-600 dark:text-surface-400">Your AI Twin</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mb-12"
      >
        {[
          { title: 'Understands Context', desc: 'Remembers your history and preferences' },
          { title: 'Speaks Your Language', desc: 'Adapts to your communication style' },
          { title: 'Always Available', desc: '24/7 assistance whenever you need it' }
        ].map((item, index) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 + index * 0.1 }}
            className="card p-4 text-left"
          >
            <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-1">
              {item.title}
            </h3>
            <p className="text-sm text-surface-600 dark:text-surface-400">
              {item.desc}
            </p>
          </motion.div>
        ))}
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        onClick={onNext}
        className="btn-primary text-lg group"
      >
        Discover How It Works
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </div>
  )
}

export default Step1
